import * as React from 'react';

import type { NegotiationHistoryFilterDto } from '@/api/negotiation-client';
import { useNegotiationHistory } from '@/api/use-negotiation';
import { Button } from '@/components/ui/button';
import { Panel, StatusDot } from '@/components/ui/panel';
import {
  NEGOTIATION_HISTORY_DOT_CLASS,
  NEGOTIATION_HISTORY_TABS,
  formatDateTimeFr,
  historyEntryText,
  plural,
} from '@/lib/negotiation-view';
import { cn } from '@/lib/utils';
import { PanelFooter, PanelMessage } from '@/routes/admin/operational-view/operational-view-table';

// =============================================================================
// negotiation-history-panel.tsx — Colonne DROITE « Historique » de la page Négociation (lot N4).
// =============================================================================
// Décision GATE_HUMAN 6 (verbatim) : « cette liste ne doit contenir que des éléments liés à la
// négociation ». Le PÉRIMÈTRE est tenu par l'API (lot N3) : l'écran n'ajoute AUCUN filtre d'exclusion —
// ce serait rendre réversible d'un clic une décision.
//
// L'API rend des DONNÉES (type, acteur, raison sociale, couples, montants) ; les libellés CHIFFRÉS sont
// composés ici par `historyEntryText`. Le filtrage et la pagination sont SERVEUR : « n événements » de
// l'en-tête est le compteur de l'ONGLET ACTIF, il reste donc cohérent avec ce que la liste montre.
//
// NE SONT PAS AFFICHÉS, faute de donnée en base (rapport DEV_API-N3 §A-1/A-2) : l'origine de
// l'ouverture (« depuis Analyse ») et la notification du fournisseur (« fournisseur notifié »).
//
// WCAG 2.1 AA : onglets = boutons `aria-pressed` dans un groupe nommé (le libellé porte l'information,
// la pastille n'est qu'un renfort décoratif `aria-hidden`), liste ordonnée, horodatage en <time>.

/** Taille de page de la colonne latérale : assez pour lire une session, assez court pour ne pas noyer. */
const HISTORY_PAGE_SIZE = 20;

export interface NegotiationHistoryPanelProps {
  readonly campaignId: string;
  /** Onglet actif (état d'URL). */
  readonly filter: NegotiationHistoryFilterDto;
  /** Page courante, 1-indexée (état d'URL). */
  readonly page: number;
  readonly onChangeFilter: (filter: NegotiationHistoryFilterDto) => void;
  readonly onChangePage: (page: number) => void;
}

export function NegotiationHistoryPanel({
  campaignId,
  filter,
  page,
  onChangeFilter,
  onChangePage,
}: NegotiationHistoryPanelProps): React.JSX.Element {
  const query = useNegotiationHistory(campaignId, {
    filter,
    page,
    pageSize: HISTORY_PAGE_SIZE,
  });

  const data = query.data;
  const total = data?.total ?? 0;
  const counts = data?.counts;
  const pageCount = Math.max(1, Math.ceil(total / HISTORY_PAGE_SIZE));

  /** Compteur de l'onglet (pastille) — fourni par l'API sur la TOTALITÉ du flux, aucun second appel. */
  const tabCount = (id: NegotiationHistoryFilterDto): number | undefined => {
    if (counts === undefined) {
      return undefined;
    }
    switch (id) {
      case 'Ouverture':
        return counts.ouverture;
      case 'Resaisie':
        return counts.resaisie;
      case 'Verrouillage':
        return counts.verrouillage;
      case 'Relance':
        return counts.relance;
      default:
        return counts.tous;
    }
  };

  return (
    <Panel
      headingId="negotiation-history-heading"
      title="Historique"
      aside={<span>{plural(total, 'événement')}</span>}
    >
      <div
        role="group"
        aria-label="Filtrer l’historique de négociation"
        className="flex flex-wrap items-center gap-2 border-b border-border px-6 py-3"
      >
        {NEGOTIATION_HISTORY_TABS.map((tab) => {
          const active = tab.id === filter;
          const count = tabCount(tab.id);
          return (
            <button
              key={tab.id}
              type="button"
              aria-pressed={active}
              onClick={() => {
                onChangeFilter(tab.id);
              }}
              className={cn(
                'rounded-full border px-2.5 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                active
                  ? 'border-foreground bg-foreground font-semibold text-background'
                  : 'border-border text-foreground hover:bg-muted',
              )}
            >
              {tab.label}
              {count === undefined ? null : (
                <span className="ml-1 font-mono tabular-nums">{String(count)}</span>
              )}
            </button>
          );
        })}
      </div>

      {query.isPending ? (
        <PanelMessage kind="status">Chargement de l’historique…</PanelMessage>
      ) : query.isError ? (
        <PanelMessage kind="alert">
          L’historique n’a pas pu être chargé : {query.error.message}
        </PanelMessage>
      ) : query.data.entries.length === 0 ? (
        <PanelMessage kind="status">
          Aucun événement de négociation pour ce filtre.
        </PanelMessage>
      ) : (
        <ol className="divide-y divide-border">
          {query.data.entries.map((entry) => {
            const text = historyEntryText(entry);
            return (
              <li key={entry.entryKey} className="flex gap-3 px-6 py-3">
                <StatusDot
                  className={cn('mt-1.5', NEGOTIATION_HISTORY_DOT_CLASS[entry.type])}
                />
                <div className="min-w-0 space-y-1">
                  <p className="text-sm leading-snug">{text.headline}</p>
                  {text.details.length > 0 ? (
                    <ul className="space-y-0.5 text-xs text-muted-foreground">
                      {text.details.map((detail) => (
                        <li key={detail}>{detail}</li>
                      ))}
                    </ul>
                  ) : null}
                  <time
                    dateTime={entry.occurredAt}
                    className="block font-mono text-[11px] tabular-nums text-muted-foreground"
                  >
                    {formatDateTimeFr(entry.occurredAt)}
                  </time>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {pageCount > 1 ? (
        <div className="flex items-center justify-between gap-3 border-t border-border px-6 py-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => {
              onChangePage(page - 1);
            }}
          >
            Précédent
          </Button>
          <p role="status" className="text-xs text-muted-foreground">
            Page {String(page)} / {String(pageCount)}
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page >= pageCount}
            onClick={() => {
              onChangePage(page + 1);
            }}
          >
            Suivant
          </Button>
        </div>
      ) : null}

      <PanelFooter
        action={
          <a
            href={`/admin/campaigns/${campaignId}/audit`}
            className="rounded underline underline-offset-4 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Tout l’historique dans Audit
          </a>
        }
      >
        Du plus récent au plus ancien · horodatage serveur.
      </PanelFooter>
    </Panel>
  );
}
